import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Sparkles } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { StatCard } from '../../components/ui/StatCard'
import { EmptyState } from '../../components/ui/EmptyState'
import pagina from '../pagine.module.css'

interface Report {
  periodo: { da: string; a: string }
  riepilogo_team: {
    sessioni_totali: number
    minuti_totali: number
    score_medio_apertura: number
    score_medio_obiezioni: number
    score_medio_chiusura: number
  }
  per_membro: Array<{
    utente: { nome: string; cognome: string; email: string } | null
    statistiche: {
      sessioni: number
      minuti_totali: number
      score_medio_apertura: number
      score_medio_obiezioni: number
      score_medio_chiusura: number
      voto_medio_complessivo: number
    }
  }>
  raccomandazioni_ai: string[]
}

export function ManagerReport() {
  const [da, setDa] = useState(() => {
    const d = new Date(Date.now() - 30 * 86400_000)
    return d.toISOString().slice(0, 10)
  })
  const [a, setA] = useState(() => new Date().toISOString().slice(0, 10))

  const q = useQuery({
    queryKey: ['manager', 'report', da, a],
    queryFn: async () =>
      (await api.get<{ dati: Report }>('/manager/report', { params: { da, a } })).data.dati,
  })

  const esporta = () => {
    if (!q.data) return
    const json = JSON.stringify(q.data, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `report-team-${da}-${a}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento report e analisi AI…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento report" />

  const r = q.data

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Report team</h1>
          <div className={pagina.sottotitolo}>
            Dati aggregati con raccomandazioni AI per il prossimo periodo di allenamento.
          </div>
        </div>
        <Button iconaSinistra={<Download size={14} />} onClick={esporta}>
          Esporta JSON
        </Button>
      </div>

      <div className={pagina.filtri}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginRight: 8 }}>Da</label>
          <input type="date" value={da} onChange={(e) => setDa(e.target.value)} className={pagina.input} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginRight: 8 }}>A</label>
          <input type="date" value={a} onChange={(e) => setA(e.target.value)} className={pagina.input} />
        </div>
      </div>

      <Card titolo="Raccomandazioni AI" azione={<Sparkles size={16} color="var(--accent)" />}>
        {r.raccomandazioni_ai.length > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {r.raccomandazioni_ai.map((c, i) => (
              <li key={i} style={{
                display: 'flex', gap: 10, padding: 12,
                background: 'var(--accent-soft)',
                borderLeft: '3px solid var(--accent)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 14, lineHeight: 1.5,
              }}>
                <strong style={{ color: 'var(--accent)', flexShrink: 0 }}>{i + 1}.</strong>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState titolo="Nessuna raccomandazione disponibile" descrizione="Servono almeno alcune sessioni nel periodo selezionato." />
        )}
      </Card>

      <section>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
          Riepilogo team
        </h3>
        <div className={pagina.griglia}>
          <StatCard etichetta="Sessioni" valore={r.riepilogo_team.sessioni_totali} />
          <StatCard etichetta="Minuti" valore={r.riepilogo_team.minuti_totali} unita="min" />
          <StatCard etichetta="Apertura" valore={r.riepilogo_team.score_medio_apertura.toFixed(1)} unita="/5" />
          <StatCard etichetta="Obiezioni" valore={r.riepilogo_team.score_medio_obiezioni.toFixed(1)} unita="/5" />
          <StatCard etichetta="Chiusura" valore={r.riepilogo_team.score_medio_chiusura.toFixed(1)} unita="/5" />
        </div>
      </section>

      <Card titolo="Performance per membro">
        {r.per_membro.length === 0 ? (
          <EmptyState titolo="Nessun dato per il periodo" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {r.per_membro.map((m, i) => (
              m.utente && (
                <div key={i} style={{
                  padding: 16, background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)',
                  display: 'grid', gridTemplateColumns: '1fr auto auto auto auto auto',
                  gap: 16, alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{m.utente.nome} {m.utente.cognome}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.utente.email}</div>
                  </div>
                  <MiniStat label="Sess." valore={m.statistiche.sessioni} />
                  <MiniStat label="Apertura" valore={m.statistiche.score_medio_apertura.toFixed(1)} unita="/5" />
                  <MiniStat label="Obiezioni" valore={m.statistiche.score_medio_obiezioni.toFixed(1)} unita="/5" />
                  <MiniStat label="Chiusura" valore={m.statistiche.score_medio_chiusura.toFixed(1)} unita="/5" />
                  <MiniStat label="Voto" valore={m.statistiche.voto_medio_complessivo.toFixed(1)} unita="/10" forte />
                </div>
              )
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function MiniStat({ label, valore, unita, forte }:
  { label: string; valore: string | number; unita?: string; forte?: boolean }) {
  return (
    <div style={{ textAlign: 'right', minWidth: 70 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{label}</div>
      <div className="mono" style={{ fontWeight: forte ? 700 : 500, fontSize: forte ? 16 : 14 }}>
        {valore}{unita ?? ''}
      </div>
    </div>
  )
}
